import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Calendar, GitCompareArrows, Plus, Search, Swords } from 'lucide-react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Customized, ReferenceArea
} from 'recharts';
import { apiService, queryKeys } from '@/services/api';
import type { DivisionInfo, DivisionPlayer } from '@/types/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { ALL_GAME_TABS, getDivisionInfoById, inferAvailableGameTabs } from '@/utils/divisionGameTabs';
import {
  buildYearRangeOptions,
  getMaxAvailableYears,
  getSelectedYearRangeValue,
} from '@/utils/historyRangeOptions';
import {
  buildCompareChartData,
  buildYearBackgroundBands,
  buildYearSections,
  getComparePointMeta,
  makeCompareRichTooltip,
  getTimelineFirstPlayedDate,
  getTimelineWinLoss,
  makeYearLabelRenderer,
  makeDateAxisTickRenderer,
  renderHistoryPointActiveDot,
  renderHistoryPointDot,
  type CompareChartRow,
  type ComparePointMeta,
} from '@/utils/historyChart';
import MatchDetailPopover, { type MatchDetail } from '@/components/MatchDetailPopover';
import HeadToHeadModal from './HeadToHeadModal';

function formatDivisionLabel(div: DivisionInfo, isCurrent: boolean): string {
  const fmt = (s: string) => {
    const d = new Date(s.includes('T') ? s : s + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };
  const dates = div.first_match_date && div.last_match_date
    ? ` (${fmt(div.first_match_date)}–${fmt(div.last_match_date)})`
    : '';
  const tag = isCurrent ? ' ★' : '';
  return `#${div.division_id}${dates}${tag}`;
}

interface ComparePlayer {
  id: string;
  name: string;
}

interface ComparePlayersModalProps {
  isOpen: boolean;
  onClose: () => void;
  players: ComparePlayer[];
  availablePlayers?: ComparePlayer[];
  onPlayersChange?: (players: ComparePlayer[]) => void;
  initialGameType?: '8-ball' | '9-ball' | '10-ball';
  teamName?: string;
  divisionId?: string;
  /** Division players for opponent name → player ID resolution (H2H). */
  divisionPlayers?: DivisionPlayer[];
}

const GAME_OPTIONS = [
  { value: '8-ball' as const, label: '8-Ball' },
  { value: '9-ball' as const, label: '9-Ball' },
  { value: '10-ball' as const, label: '10-Ball' },
];

const TIME_OPTIONS: { value: number | undefined; label: string }[] = [
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 180, label: '6mo' },
];

// Distinct colors for up to 10 players
const PLAYER_COLORS = [
  '#2563eb', '#f59e0b', '#7c3aed', '#475569', '#4338ca',
  '#ea580c', '#1d4ed8', '#a855f7', '#92400e', '#0f172a',
];

export const ComparePlayersModal: React.FC<ComparePlayersModalProps> = ({
  isOpen,
  onClose,
  players,
  availablePlayers,
  onPlayersChange,
  initialGameType = '8-ball',
  teamName,
  divisionId,
  divisionPlayers,
}) => {
  const [gameType, setGameType] = useState<'8-ball' | '9-ball' | '10-ball'>(initialGameType);
  const [days, setDays] = useState<number | undefined>(divisionId ? undefined : 90);
  const [hiddenPlayers, setHiddenPlayers] = useState<Set<string>>(new Set());
  const [selectedDivision, setSelectedDivision] = useState<string | undefined>(divisionId);
  const [groupMatchDays, setGroupMatchDays] = useState(true);
  const [focusedTooltipPlayerId, setFocusedTooltipPlayerId] = useState<string | null>(null);
  const [hoveredPlayerId, setHoveredPlayerId] = useState<string | null>(null);
  const [selectedMatch, setSelectedMatch] = useState<{ detail: MatchDetail; x: number; y: number; playerId?: string } | null>(null);
  const [activePlayers, setActivePlayers] = useState<ComparePlayer[]>(players);
  const [isAddPlayerOpen, setIsAddPlayerOpen] = useState(false);
  const [playerSearch, setPlayerSearch] = useState('');
  const [h2hTarget, setH2hTarget] = useState<{ playerAId: string; playerAName: string; playerBId: string; playerBName: string } | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setActivePlayers(players);
    setHiddenPlayers(new Set());
    setFocusedTooltipPlayerId(null);
    setIsAddPlayerOpen(false);
    setPlayerSearch('');
  }, [isOpen, players]);

  const updateActivePlayers = (nextPlayers: ComparePlayer[]) => {
    setActivePlayers(nextPlayers);
    onPlayersChange?.(nextPlayers);
    setHiddenPlayers((prev) => new Set([...prev].filter((playerId) => nextPlayers.some((player) => player.id === playerId))));
    setFocusedTooltipPlayerId((current) => (current && nextPlayers.some((player) => player.id === current) ? current : null));
  };

  const removePlayer = (playerId: string) => {
    updateActivePlayers(activePlayers.filter((player) => player.id !== playerId));
  };

  // H2H: resolve opponent name to player ID and launch H2H modal
  const handlePopoverHeadToHead = useCallback(
    (triggeredPlayerId: string, opponentName: string) => {
      // Normalize: lowercase, collapse multiple spaces, trim
      const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
      const normalizedOpponent = norm(opponentName);

      const nameMatch = (playerName: string) => {
        const n = norm(playerName);
        return n === normalizedOpponent || n.includes(normalizedOpponent) || normalizedOpponent.includes(n);
      };

      // Search active players, then available players, then division players
      let opponent: { id: string; name: string } | undefined =
        activePlayers.find((p) => nameMatch(p.name));
      if (!opponent && availablePlayers) {
        opponent = availablePlayers.find((p) => nameMatch(p.name));
      }
      if (!opponent && divisionPlayers) {
        const dp = divisionPlayers.find((p) => nameMatch(p.name));
        if (dp) opponent = { id: dp.player_id, name: dp.name };
      }

      if (!opponent) {
        console.warn(`[H2H] Could not resolve opponent "${opponentName}" to a player ID`);
        alert(`Could not find player "${opponentName}" for Head to Head comparison.`);
        return;
      }
      const triggerPlayer = activePlayers.find((p) => p.id === triggeredPlayerId);
      if (!triggerPlayer) {
        console.warn(`[H2H] Could not find trigger player "${triggeredPlayerId}" in active players`);
        return;
      }
      setSelectedMatch(null);
      setH2hTarget({
        playerAId: triggerPlayer.id,
        playerAName: triggerPlayer.name,
        playerBId: opponent.id,
        playerBName: opponent.name,
      });
    },
    [activePlayers, availablePlayers, divisionPlayers]
  );

  const selectablePlayers = useMemo(() => availablePlayers ?? players, [availablePlayers, players]);
  const addablePlayers = useMemo(
    () => selectablePlayers.filter((player) => !activePlayers.some((activePlayer) => activePlayer.id === player.id)),
    [activePlayers, selectablePlayers]
  );
  const filteredAddablePlayers = useMemo(() => {
    const normalizedSearch = playerSearch.trim().toLowerCase();
    if (!normalizedSearch) return addablePlayers;

    return addablePlayers.filter((player) =>
      player.name.toLowerCase().includes(normalizedSearch) || player.id.toLowerCase().includes(normalizedSearch)
    );
  }, [addablePlayers, playerSearch]);

  const { data: divisions } = useQuery({
    queryKey: queryKeys.divisions,
    queryFn: () => apiService.getDivisions(),
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  });

  const selectedDivisionInfo = useMemo(
    () => getDivisionInfoById(divisions, selectedDivision),
    [divisions, selectedDivision]
  );

  const availableGameOptions = useMemo(() => {
    const tabs = inferAvailableGameTabs(selectedDivisionInfo?.name);
    return GAME_OPTIONS.filter((option) => tabs.includes(option.value.replace('-', '') as typeof ALL_GAME_TABS[number]));
  }, [selectedDivisionInfo?.name]);

  useEffect(() => {
    if (!availableGameOptions.some((option) => option.value === gameType)) {
      setGameType(availableGameOptions[0]?.value ?? '8-ball');
    }
  }, [availableGameOptions, gameType]);

  const togglePlayer = (playerId: string) => {
    setHiddenPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  // Fetch per-match CSR timeline for each player
  const csrQueries = useQueries({
    queries: activePlayers.map((player) => ({
      queryKey: queryKeys.csrTimeline(player.id, gameType, selectedDivision ? undefined : days, selectedDivision),
      queryFn: () => apiService.getCSRTimeline(player.id, gameType, selectedDivision ? undefined : days, selectedDivision),
      staleTime: 5 * 60 * 1000,
      enabled: isOpen,
    })),
  });

  const fullRangeQueries = useQueries({
    queries: activePlayers.map((player) => ({
      queryKey: queryKeys.csrTimeline(player.id, gameType, undefined, undefined),
      queryFn: () => apiService.getCSRTimeline(player.id, gameType, undefined, undefined),
      staleTime: 5 * 60 * 1000,
      enabled: isOpen && !selectedDivision,
    })),
  });

  const isLoading = csrQueries.some((q) => q.isLoading);
  const hasError = csrQueries.every((q) => q.error);

  // Players currently visible (not hidden) — used for H2H eligibility
  const visiblePlayers = useMemo(
    () => activePlayers.filter((p) => !hiddenPlayers.has(p.id)),
    [activePlayers, hiddenPlayers],
  );

  const chartData = useMemo(() => {
    return buildCompareChartData(
      activePlayers.map((player, idx) => {
        const visibleTimeline = csrQueries[idx].data?.timeline;
        const fullTimeline = fullRangeQueries[idx]?.data?.timeline;
        return {
          playerId: player.id,
          timeline: visibleTimeline,
          earliestActualDate: getTimelineFirstPlayedDate(fullTimeline ?? visibleTimeline),
        };
      }),
      {
        padMatchDays: Boolean(selectedDivision) && groupMatchDays,
        groupSameDayMatches: groupMatchDays,
      },
    );
  }, [activePlayers, csrQueries, fullRangeQueries]);

  const playerLineStats = useMemo(
    () => new Map(activePlayers.map((player, idx) => [
      player.id,
      getTimelineWinLoss(csrQueries[idx]?.data?.timeline),
    ])),
    [activePlayers, csrQueries],
  );

  const axisTick = useMemo(
    () => makeDateAxisTickRenderer(new Map(
      chartData
        .filter((point) => !point.isSpacer)
        .map((point) => [point.xKey, point])
    )),
    [chartData]
  );

  const fullDateDisplayByDate = useMemo(
    () => new Map(
      chartData
        .filter((point) => !point.isSpacer)
        .map((point) => [point.xKey, point.fullDateDisplay])
    ),
    [chartData]
  );

  const tooltipContent = useMemo(
    () => makeCompareRichTooltip(
      fullDateDisplayByDate,
      (playerId) => activePlayers.find((player) => player.id === playerId)?.name ?? playerId,
      (playerId) => {
        const idx = activePlayers.findIndex((p) => p.id === playerId);
        return PLAYER_COLORS[idx >= 0 ? idx % PLAYER_COLORS.length : 0];
      },
      focusedTooltipPlayerId,
      selectedMatch,
      handlePopoverHeadToHead,
    ),
    [activePlayers, focusedTooltipPlayerId, fullDateDisplayByDate, selectedMatch, handlePopoverHeadToHead]
  );

  const yearRangeOptions = useMemo(
    () => buildYearRangeOptions(getMaxAvailableYears(fullRangeQueries.map((query) => query.data?.timeline))),
    [fullRangeQueries]
  );

  const selectedYearRangeValue = useMemo(
    () => getSelectedYearRangeValue(days),
    [days]
  );

  const yearBands = useMemo(
    () => buildYearBackgroundBands(chartData.map((point) => point.date)).map((band) => ({
      ...band,
      startDate: chartData.find((point) => point.date === band.startDate)?.xKey ?? band.startDate,
      endDate: [...chartData].reverse().find((point) => point.date === band.endDate)?.xKey ?? band.endDate,
    })),
    [chartData]
  );

  const yearSections = useMemo(
    () => buildYearSections(chartData.map((point) => point.date)).map((section) => ({
      ...section,
      startDate: chartData.find((point) => point.date === section.startDate)?.xKey ?? section.startDate,
      endDate: [...chartData].reverse().find((point) => point.date === section.endDate)?.xKey ?? section.endDate,
    })),
    [chartData]
  );

  const yearLabelRenderer = useMemo(
    () => makeYearLabelRenderer(yearSections),
    [yearSections]
  );

  const sameDayBands = useMemo(() => {
    if (!groupMatchDays) return [];

    const rowsByDate = new Map<string, CompareChartRow[]>();
    chartData.filter((row) => !row.isSpacer && row.isGroupedMatchDay).forEach((row) => {
      rowsByDate.set(row.date, [...(rowsByDate.get(row.date) ?? []), row]);
    });
    return [...rowsByDate.entries()]
      .map(([date]) => ({
        x1: `${date}#before`,
        x2: `${date}#after`,
        label: new Date(`${date}T12:00:00`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
        }),
      }));
  }, [chartData, groupMatchDays]);

  // Stable Y-axis domain computed from ALL active players, so hiding a
  // player from the chart never rescales the axis (would skew the comparison
  // of remaining players).
  const yDomain = useMemo<[number | string, number | string]>(() => {
    let min = Infinity;
    let max = -Infinity;
    for (const row of chartData) {
      for (const player of activePlayers) {
        const v = row[player.id];
        if (typeof v === 'number') {
          if (v < min) min = v;
          if (v > max) max = v;
        }
      }
    }
    if (!isFinite(min) || !isFinite(max)) return ['dataMin - 2', 'dataMax + 2'];
    return [min - 2, max + 2];
  }, [chartData, activePlayers]);

  const visibleStartMarkers = useMemo(
    () => activePlayers
      .filter((player) => !hiddenPlayers.has(player.id))
      .map((player, idx) => {
        const color = PLAYER_COLORS[idx % PLAYER_COLORS.length];
        const firstRow = chartData.find((row) => typeof row[player.id] === 'number');
        const value = firstRow?.[player.id];
        return typeof value === 'number'
          ? { playerId: player.id, value, color }
          : null;
      })
      .filter((marker): marker is { playerId: string; value: number; color: string } => marker !== null),
    [activePlayers, chartData, hiddenPlayers]
  );

  const makeCompareDotClickHandler = (playerId: string, playerColor: string, props: any) => {
    const meta = getComparePointMeta(props.payload as CompareChartRow, playerId) as ComparePointMeta | undefined;
    if (!meta || meta.pointType === 'missed') return undefined;
    return () => {
      const row = props.payload as CompareChartRow;
      const player = activePlayers.find((p) => p.id === playerId);
      setSelectedMatch({
        detail: {
          date: row.date,
          result: meta.result,
          csr: Math.round(Number(row[playerId] ?? 0)),
          opponent_name: meta.opponent_name,
          player_games_won: meta.player_games_won,
          opponent_games_won: meta.opponent_games_won,
          player_race: meta.player_race,
          opponent_race: meta.opponent_race,
          venue: meta.venue,
          division_name: meta.division_name,
          is_makeup_match: meta.is_makeup_match,
          csr_before: meta.csr_before,
          playerName: player?.name,
          playerColor,
        },
        x: props.cx,
        y: props.cy,
        playerId,
      });
    };
  };

  const renderPlayerDot = (playerId: string, playerColor: string) => (props: any) => {
    const meta = getComparePointMeta(props.payload as CompareChartRow, playerId) as ComparePointMeta | undefined;
    if (!meta || props.value == null) return <g />;

    const onClick = makeCompareDotClickHandler(playerId, playerColor, props);
    const baseProps = {
      ...props,
      payload: meta,
      onMouseEnter: () => setFocusedTooltipPlayerId(playerId),
      onMouseLeave: () => setFocusedTooltipPlayerId((current) => (current === playerId ? null : current)),
      ...(onClick ? { onClick } : {}),
    };

    return renderHistoryPointDot(baseProps);
  };

  if (!isOpen) return null;

  const renderPlayerActiveDot = (playerId: string, playerColor: string) => (props: any) => {
    const meta = getComparePointMeta(props.payload as CompareChartRow, playerId);
    if (!meta || props.value == null) return <g />;

    const onClick = makeCompareDotClickHandler(playerId, playerColor, props);
    return renderHistoryPointActiveDot({
      ...props,
      payload: meta,
      onMouseEnter: () => setFocusedTooltipPlayerId(playerId),
      onMouseLeave: () => setFocusedTooltipPlayerId((current) => (current === playerId ? null : current)),
      ...(onClick ? { onClick } : {}),
    });
  };

  const renderStartMarkers = (props: any) => {
    const yAxis = Object.values(props?.yAxisMap ?? {})[0] as { scale?: (value: number) => number } | undefined;
    const offset = props?.offset as { left?: number } | undefined;
    const scale = yAxis?.scale;
    if (!scale || offset?.left == null) return null;

    const markerX = offset.left - 18;
    const tickEndX = offset.left - 4;

    return (
      <g>
        {visibleStartMarkers.map((marker) => (
          <g key={marker.playerId}>
            <line
              x1={markerX + 7}
              y1={scale(marker.value)}
              x2={tickEndX}
              y2={scale(marker.value)}
              stroke={marker.color}
              strokeWidth={2}
              strokeLinecap="round"
            />
            <circle
              cx={markerX}
              cy={scale(marker.value)}
              r={6}
              fill={marker.color}
              stroke="#ffffff"
              strokeWidth={2}
            />
          </g>
        ))}
      </g>
    );
  };

  return (
    <>
    <div className="fixed inset-0 z-50 modal-backdrop flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div
        className="bg-white w-full h-full md:h-auto md:rounded-2xl shadow-2xl md:max-w-5xl md:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-50 rounded-lg">
              <GitCompareArrows className="w-5 h-5 text-brand-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                Compare Player CSRs
                {teamName && <span className="text-slate-500 font-normal"> — {teamName}</span>}
              </h2>
              <p className="text-sm text-slate-400">
                {activePlayers.length} players{teamName ? ` from ${teamName}` : ' selected'}
                {selectedDivision && divisions ? (() => {
                  const div = divisions.find((d: DivisionInfo) => d.division_id === selectedDivision);
                  return div ? ` · ${div.name || `Division ${div.division_id}`}` : '';
                })() : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 md:gap-4 px-4 md:px-6 py-3 border-b border-slate-100 bg-slate-50/50">
          {/* Game Type */}
          <div className="flex gap-1 p-0.5 bg-slate-200/60 rounded-lg">
            {availableGameOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setGameType(opt.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  gameType === opt.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Time Range — hidden when division is selected */}
          {!selectedDivision && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-400" />
              <div className="flex gap-1 p-0.5 bg-slate-200/60 rounded-lg">
                {TIME_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setDays(opt.value)}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-all ${
                      days === opt.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <select
                value={selectedYearRangeValue}
                onChange={(e) => {
                  const option = yearRangeOptions.find((entry) => entry.value === e.target.value);
                  setDays(option?.days);
                }}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-slate-200/60 border-0 text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-300"
              >
                <option value="" disabled>Years</option>
                {yearRangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Division/Season filter */}
          {divisions && divisions.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                value={selectedDivision || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedDivision(val || undefined);
                }}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-slate-200/60 border-0 text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-300"
              >
                <option value="">All Divisions</option>
                {divisions.map((div: DivisionInfo, idx: number) => (
                  <option key={div.division_id} value={div.division_id}>
                    {formatDivisionLabel(div, idx === 0)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
            <input
              type="checkbox"
              checked={groupMatchDays}
              onChange={(e) => setGroupMatchDays(e.target.checked)}
              className="rounded border-slate-300 text-brand-600 focus:ring-brand-300"
            />
            Group match days
          </label>

          {/* Head to Head button (when exactly 2 visible players) */}
          {visiblePlayers.length === 2 && (
            <button
              onClick={() => {
                setH2hTarget({
                  playerAId: visiblePlayers[0].id,
                  playerAName: visiblePlayers[0].name,
                  playerBId: visiblePlayers[1].id,
                  playerBName: visiblePlayers[1].name,
                });
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors border border-amber-200"
            >
              <Swords className="w-3.5 h-3.5" />
              Head to Head
            </button>
          )}
        </div>

        {/* Chart */}
        <div className="flex-1 overflow-auto p-3 md:p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-80">
              <LoadingSpinner size="lg" message="Loading player data..." />
            </div>
          ) : hasError ? (
            <div className="flex items-center justify-center h-80 text-red-500 text-sm">
              Failed to load some player data. Try again later.
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex items-center justify-center h-80">
              <div className="text-center">
                <p className="text-slate-500 font-medium">No comparison data</p>
                <p className="text-slate-400 text-sm mt-1">
                  {activePlayers.length === 0
                    ? 'No players are currently in this comparison.'
                    : `None of the selected players have ${gameType} history in this time range.`}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Legend / Player list */}
              <div className="flex flex-wrap gap-3 mb-5">
                {activePlayers.map((player, idx) => {
                  const isHidden = hiddenPlayers.has(player.id);
                  return (
                    <div
                      key={player.id}
                      className={`flex items-center gap-2 text-sm cursor-pointer transition-opacity ${isHidden ? 'opacity-35' : ''}`}
                    onClick={() => togglePlayer(player.id)}
                  >
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PLAYER_COLORS[idx % PLAYER_COLORS.length] }} />
                      <span className="font-medium text-slate-700">{player.name}</span>
                    </div>
                  );
                })}
              </div>

              {/* Chart */}
              <div className="relative h-72 sm:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 16, bottom: 12 }}
                    onMouseLeave={() => setFocusedTooltipPlayerId(null)}
                  >
                    {yearBands.map((band) => (
                      <ReferenceArea
                        key={band.year}
                        x1={band.startDate}
                        x2={band.endDate}
                        fill="#f8fafc"
                        fillOpacity={0.9}
                        strokeOpacity={0}
                      />
                    ))}
                    {sameDayBands.map((band) => (
                      <ReferenceArea
                        key={band.x1}
                        x1={band.x1}
                        x2={band.x2}
                        fill="#eef2ff"
                        fillOpacity={0.8}
                        stroke="#c7d2fe"
                        strokeOpacity={0.8}
                        label={{
                          value: band.label,
                          position: 'insideTop',
                          fill: '#6366f1',
                          fontSize: 10,
                        }}
                      />
                    ))}
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="xKey"
                      tick={axisTick}
                      tickLine={false}
                      axisLine={false}
                      height={yearSections.length > 1 ? 82 : 48}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      fontSize={11}
                      stroke="#94a3b8"
                      tickLine={false}
                      axisLine={false}
                      domain={yDomain}
                      allowDataOverflow
                      width={52}
                    />
                    <Tooltip content={tooltipContent} />
                    <Customized component={yearLabelRenderer} />
                    <Customized component={renderStartMarkers} />
                    {activePlayers.filter((p) => !hiddenPlayers.has(p.id)).map((player, _idx) => {
                      const colorIdx = activePlayers.indexOf(player);
                      const isHovered = hoveredPlayerId === player.id;
                      const isDimmed = hoveredPlayerId !== null && !isHovered;
                      return (
                        <Line
                          key={player.id}
                          type="monotone"
                          dataKey={player.id}
                          stroke={PLAYER_COLORS[colorIdx % PLAYER_COLORS.length]}
                          strokeWidth={isHovered ? 4 : 2.5}
                          strokeOpacity={isDimmed ? 0.2 : 1}
                           dot={renderPlayerDot(player.id, PLAYER_COLORS[colorIdx % PLAYER_COLORS.length])}
                           activeDot={renderPlayerActiveDot(player.id, PLAYER_COLORS[colorIdx % PLAYER_COLORS.length])}
                          name={player.id}
                          connectNulls
                          isAnimationActive={false}
                        />
                      );
                    })}
                  </LineChart>
                </ResponsiveContainer>
                {selectedMatch && (
                  <span className="absolute left-0 top-0 w-0 h-0" data-popover-anchor />
                )}
              </div>

              {sameDayBands.length > 0 && (
                <p className="mt-2 text-xs text-slate-400">
                  Shaded bands mark match days.
                </p>
              )}

              {/* Current values summary */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-400">{activePlayers.length} players</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setIsAddPlayerOpen((current) => !current)}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-brand-600 hover:bg-brand-50 hover:text-brand-700 transition-colors disabled:text-slate-300 disabled:hover:bg-transparent"
                      disabled={addablePlayers.length === 0}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      Add player
                    </button>
                    <button
                      onClick={() => {
                        const allHidden = activePlayers.every(p => hiddenPlayers.has(p.id));
                        setHiddenPlayers(allHidden ? new Set() : new Set(activePlayers.map(p => p.id)));
                      }}
                      className="text-[10px] font-medium text-slate-400 hover:text-slate-600 transition-colors px-1.5 py-0.5 rounded hover:bg-slate-100"
                    >
                      {activePlayers.every(p => hiddenPlayers.has(p.id)) ? 'Show all' : 'Hide all'}
                    </button>
                  </div>
                </div>
                {isAddPlayerOpen && (
                  <div className="mb-3 rounded-xl border border-slate-200 bg-slate-50/70 p-3">
                    <label className="mb-2 block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Add player
                    </label>
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                      <input
                        value={playerSearch}
                        onChange={(event) => setPlayerSearch(event.target.value)}
                        placeholder="Search by player name or ID..."
                        className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 outline-none transition-colors focus:border-brand-300 focus:ring-2 focus:ring-brand-100"
                      />
                    </div>
                    <div className="mt-3 max-h-40 space-y-2 overflow-y-auto">
                      {filteredAddablePlayers.length > 0 ? (
                        filteredAddablePlayers.map((player) => (
                          <button
                            key={player.id}
                            onClick={() => {
                              updateActivePlayers([...activePlayers, player]);
                              setPlayerSearch('');
                              setIsAddPlayerOpen(false);
                            }}
                            className="flex w-full items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:border-brand-300 hover:bg-brand-50/40"
                          >
                            <div>
                              <div className="font-medium text-slate-800">{player.name}</div>
                              <div className="text-xs text-slate-400">#{player.id}</div>
                            </div>
                            <Plus className="h-4 w-4 text-brand-500" />
                          </button>
                        ))
                      ) : (
                        <div className="rounded-lg border border-dashed border-slate-200 bg-white px-3 py-4 text-center text-sm text-slate-400">
                          {addablePlayers.length === 0 ? 'All available players are already in this comparison.' : 'No players match your search.'}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {activePlayers.map((player, idx) => {
                    const firstEntry = chartData.find((d) => d[player.id] != null);
                    const latestEntry = [...chartData].reverse().find((d) => d[player.id] != null);
                    const firstRawVal = firstEntry?.[player.id];
                    const rawVal = latestEntry?.[player.id];
                    const firstVal = typeof firstRawVal === 'number' ? firstRawVal : undefined;
                    const val = typeof rawVal === 'number' ? rawVal : undefined;
                    const csrChange = val != null && firstVal != null ? val - firstVal : undefined;
                    const stats = playerLineStats.get(player.id) ?? { wins: 0, losses: 0 };
                    const matchesPlayed = stats.wins + stats.losses;
                    const winPercentage = matchesPlayed
                      ? Math.round((stats.wins / matchesPlayed) * 100)
                      : 0;
                    const isHidden = hiddenPlayers.has(player.id);
                    return (
                      <div
                        key={player.id}
                        className={`relative flex items-center gap-2 rounded-lg p-2 pr-8 cursor-pointer select-none transition-all ${
                          isHidden
                            ? 'bg-slate-100 opacity-50'
                            : hoveredPlayerId === player.id
                              ? 'bg-brand-50 ring-2 ring-brand-300'
                              : 'bg-slate-50 hover:bg-slate-100'
                        }`}
                        onClick={() => togglePlayer(player.id)}
                        onMouseEnter={() => !isHidden && setHoveredPlayerId(player.id)}
                        onMouseLeave={() => setHoveredPlayerId((current) => (current === player.id ? null : current))}
                      >
                        <button
                          type="button"
                          aria-label={`Remove ${player.name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            removePlayer(player.id);
                          }}
                          className="absolute right-2 top-2 rounded-md p-1 text-slate-300 hover:bg-white hover:text-slate-500 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                        <div
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: isHidden ? '#94a3b8' : PLAYER_COLORS[idx % PLAYER_COLORS.length] }}
                        />
                        <div className="min-w-0">
                          <div className={`text-xs truncate ${isHidden ? 'text-slate-400' : 'text-slate-400'}`}>{player.name}</div>
                          <div className={`flex items-baseline gap-1.5 text-sm font-bold ${isHidden ? 'text-slate-400' : 'text-slate-800'}`}>
                            {val != null ? val : '—'}
                            {csrChange != null && (
                              <span className={`text-[11px] font-semibold ${
                                isHidden
                                  ? 'text-slate-400'
                                  : csrChange > 0
                                    ? 'text-emerald-600'
                                    : csrChange < 0
                                      ? 'text-red-500'
                                      : 'text-slate-400'
                              }`}>
                                {csrChange > 0 ? '+' : ''}{csrChange} CSR
                              </span>
                            )}
                          </div>
                          <div className={`text-[11px] font-medium ${isHidden ? 'text-slate-400' : 'text-slate-500'}`}>
                            <span className={isHidden ? undefined : 'text-emerald-600'}>{stats.wins}W</span>
                            <span className="mx-1 text-slate-300">·</span>
                            <span className={isHidden ? undefined : 'text-red-500'}>{stats.losses}L</span>
                            <span className="mx-1 text-slate-300">·</span>
                            <span>{winPercentage}%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* MatchDetailPopover — rendered OUTSIDE the overflow-hidden modal shell
          so mobile Safari touch events reach the button correctly */}
      {selectedMatch && (
        <MatchDetailPopover
          detail={selectedMatch.detail}
          x={selectedMatch.x}
          y={selectedMatch.y}
          onClose={() => setSelectedMatch(null)}
          onHeadToHead={selectedMatch.playerId
            ? (opponentName) => handlePopoverHeadToHead(selectedMatch.playerId!, opponentName)
            : undefined
          }
        />
      )}
    </div>

    {/* Head to Head Modal */}
    {h2hTarget && (
      <HeadToHeadModal
        isOpen={true}
        onClose={() => setH2hTarget(null)}
        playerAId={h2hTarget.playerAId}
        playerAName={h2hTarget.playerAName}
        playerBId={h2hTarget.playerBId}
        playerBName={h2hTarget.playerBName}
        initialGameType={gameType}
        divisionId={divisionId}
      />
    )}
    </>
  );
};

export default ComparePlayersModal;
